// @ts-check
import { getCasePropSelector } from '../utils/case-prop-selector'
import { createSelector } from '../utils/selector-utils'

/** @type {Record<SystemsReviewCategory, string>} */
export const systemsReviewCategoryMap = {
  general: 'General',
  cardiac: 'Cardiac System',
  pulmonary: 'Pulmonary System',
  hepatic: 'Hepatic System',
  renal: 'Renal System',
  hematologic: 'Hematological System',
  endocrine: 'Endocrine System',
  oncology: 'Oncology',
  cns: 'Central Nervous System',
  muscular: 'Muscular/Skeletal System',
  gastro: 'Gastrointestinal System',
  psychiatric: 'Psychiatric/Psychological',
  gyn: 'Gynecological System',
  developmental: 'Developmental Challenges',
  habits: 'Habits',
}

/** @type {CaseSelector<SystemsReviewRaw>} */
export const selectSystemsReviewRaw = caseData => caseData.systemsReview || {}

/** @type {CaseSelector<boolean>} */
export const selectIsHealthy = createSelector(
  selectSystemsReviewRaw,
  raw =>
    Object.keys(raw).length === Object.keys(systemsReviewCategoryMap).length &&
    Object.values(raw).every(system => system.hasEntries === false)
)

/**
 * @param {SystemsReviewCategory} category
 * @returns {any} Updates to apply to case
 */
export const actionSetSystemCategoryToNo = category => ({
  [`systemsReview.${category}.entries`]: null,
  [`systemsReview.${category}.hasEntries`]: false,
})

/**
 * @param {{
 *   category: SystemsReviewCategory
 *   index: number
 *   detailIndex: number
 *   name: string
 *   value: string
 *   path: string
 *   label
 * }} opts
 * @returns {any} Updates to apply to case
 */
export const actionUpdateSystemsReviewDetail = ({
  category,
  index,
  detailIndex,
  name,
  value,
  path,
  label,
}) => ({
  [`systemsReview.${category}.entries.[${index}].details.${detailIndex}`]: {
    name,
    value,
    path,
    label,
  },
})

/**
 * @param {SystemsReviewCategory} category
 * @param {number} index
 * @param {boolean} isEditing
 * @returns {any} Updates to apply to case
 */
export const actionEditSystemQuestion = (category, index, isEditing) => ({
  [`systemsReview.${category}.entries.[${index}].editing`]: !!isEditing,
})

/**
 * @param {SystemsReviewCategory} category
 * @param {number} index
 * @param {boolean} isLastEntry
 * @returns {any} Updates to apply to case
 */
export const actionRemoveSystemQuestions = (category, index, isLastEntry) => ({
  [`systemsReview.${category}.entries.[${index}]`]: null,
  [`systemsReview.${category}.hasEntries`]: isLastEntry ? null : true,
})

/**
 * @param {SystemsReviewCategory} category
 * @param {number} index
 * @returns {any} Updates to apply to case
 */
export const actionAddSystemQuestions = (category, index) => ({
  [`systemsReview.${category}.entries.[${index}]`]: {
    editing: true,
  },
  [`systemsReview.${category}.hasEntries`]: true,
})

/** @type {SystemsReviewCategory[]} */
const allSystemCategories = [
  'cardiac',
  'cns',
  'developmental',
  'endocrine',
  'gastro',
  'general',
  'gyn',
  'habits',
  'hematologic',
  'hepatic',
  'muscular',
  'oncology',
  'psychiatric',
  'pulmonary',
  'renal',
]
/** @returns {any} Updates to apply to case */
export const actionSetAllSystemCategoriesToNo = () =>
  allSystemCategories.reduce(
    (obj, category) =>
      Object.assign(obj, actionSetSystemCategoryToNo(category)),
    {}
  )

/** @type {CaseSelector<{}>} */
export const selectSystemsReviewItemsByCategory = createSelector(
  selectSystemsReviewRaw,
  items => {
    const res = {}
    for (const key in items) {
      if (!res[key]) {
        res[key] = []
      }
      res[key] = items[key].entries || []
    }
    return res
  }
)

export const selectSystemsReviewProcessed = createSelector(
  selectSystemsReviewItemsByCategory,
  raw => {
    const res = {}
    for (const key in systemsReviewCategoryMap) {
      const entries = raw[key]
      if (entries) {
        res[key] = []
        for (const entry of entries) {
          const editing = entry.editing
          const details = entry.details || []

          const questionObject = { editing }
          res[key].push(questionObject)

          // We loop this way because it works for both arrays and objects
          // At on point `details` were arrays and not objects.
          // But we changed that because it caused some issues.
          // So we loop through with a `for ... in` which will work
          // for both arrays and objects.
          for (let keyOrIndex in details) {
            const detail = details[keyOrIndex]
            const { name, label, path, value } = detail
            questionObject[name] = {
              name,
              valueDescription: label,
              path,
              value,
            }
          }
        }
      } else {
        res[key] = null
      }
    }
    return res
  }
)

const formatSystemReviewValues = arr => {
  const values = arr
    .map(value => (value && value.trim ? value.trim() : value))
    .filter(x => x)
  return values.length > 1
    ? values[0] + ': ' + values.slice(1).join(', ')
    : values[0]
}

/** @type {CaseSelector<SystemsReviewCategorySummary | null>} */
export const selectSystemsReviewSummariesByCategory = createSelector(
  selectSystemsReviewProcessed,
  systemsReview => {
    const toReturn = {}
    let hasAnyValues = false
    for (const key in systemsReview) {
      let issues = systemsReview[key]
      if (issues) {
        toReturn[key] = {
          name: key,
          label: systemsReviewCategoryMap[key],
          summaries: [],
        }
        issues.forEach(issueValuesByName => {
          hasAnyValues = true
          const values = []
          for (const key in issueValuesByName) {
            const { valueDescription } = issueValuesByName[key]
            if (valueDescription) {
              values.push(valueDescription)
            }
          }
          const toPush = formatSystemReviewValues(values)
          if (toPush) {
            toReturn[key].summaries.push(formatSystemReviewValues(values))
          }
        })
      }
    }
    if (!hasAnyValues) {
      return null
    }
    return toReturn
  }
)

/** @type {CaseSelector<SystemsReviewSummary | null>} */
export const selectSystemsReviewSummary = createSelector(
  selectSystemsReviewSummariesByCategory,
  getCasePropSelector('systemsReviewSkipped', false),
  selectIsHealthy,
  (systemsReviewSummariesByCategory, skipped, isHealthy) => {
    const buff = []
    if (skipped) {
      return null
    }
    if (isHealthy) {
      return buff
    }
    if (!systemsReviewSummariesByCategory) {
      return null
    }
    for (const key in systemsReviewSummariesByCategory) {
      const entry = systemsReviewSummariesByCategory[key]
      if (!entry || !entry.summaries.length) {
        continue
      }
      const cat = entry.label
      const catEntries = []
      for (const summary of entry.summaries) {
        catEntries.push(summary)
      }
      buff.push([cat, catEntries])
    }
    if (!buff.length) {
      return null
    }
    return buff
  }
)
